import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import API_URL from "../utils";
import GlobalStyle from "../components/styles/Globalstyle";
import {
  DashboardContainer,
  MobileOverlay,
  MainContent,
  ErrorMessage,
} from "../components/styles/Layoutstyle";

import { formatDate } from "../utils/utils";
import Header from "../components/modules/Header";
import Analytics from "../components/modules/Analytics";
import SearchResultsIndicator from "../components/modules/SearchResultsIndicator";
import OrdersTable from "../components/modules/OrdersTable";
import MobileOrders from "../components/modules/MobileOrders";
import Pagination from "../components/modules/Pagination";
import DashboardSidebar from "../components/modules/Sidebar";
import AccuzipQueue from "../components/modules/Accuzip";
import UsersManagement from "./User/UsersManagement";
import RealflowCsvPage from "../components/modules/RealflowCsvPage";

function Dashboard() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  // const [pageSize, setPageSize] = useState(50);
  const [streetViewOnly, setStreetViewOnly] = useState(false);
  const [statusOrderRosOnly, setStatusOrderRosOnly] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 0,
    page: 1,
    pageSize: 50,
  });

  // Search + filters
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [accuzippQueue, setAccuzippQueue] = useState([]);
  const [statusesLoading, setStatusesLoading] = useState(false);
  const [columnFilters, setColumnFilters] = useState({});

  const [activeView, setActiveView] = useState("dashboard");
  const [analytics, setAnalytics] = useState({
    totalOrders: 0,
    completedOrders: 0,
    processingOrders: 0,
    pendingOrders: 0,
    statusOrderRosCount: 0,
    streetViewRequiredCount: 0,
  });
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Initialize from localStorage, default to false if not set
    const savedMode = localStorage.getItem("isDarkMode");
    return savedMode === "true";
  });
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const navigate = useNavigate();
  const searchTimeoutRef = useRef(null);
  const fetchController = useRef(null);
  const firstRenderRef = useRef(true);

  const username = localStorage.getItem("user") || "Admin";
  const [isMobile, setIsMobile] = useState(false);
  const isMobileRef = useRef(false);

  const [userDate, setUserDate] = useState("user");

  useEffect(() => {
    const userData = localStorage?.getItem("user");
    const parsed = JSON.parse(userData);
    setUserDate(parsed || {});
  }, []);
  useEffect(() => {
    const checkScreenSize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      isMobileRef.current = mobile;
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Save dark mode preference to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("isDarkMode", isDarkMode);
  }, [isDarkMode]);

  // ✅ Fetch Orders with unified column filters
  const fetchOrders = async (
    page = 1,
    search = "",
    status = "",
    filters = "{}",
    streetViewOnly = false, // NEW PARAMETER
    statusOrderRosOnly = false, // NEW PARAMETER
    signal
  ) => {
    setLoading(true);
    setError("");
    setIsSearching(
      Boolean(
        search ||
          status ||
          filters !== "{}" ||
          streetViewOnly ||
          statusOrderRosOnly
      )
    );

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/");
        return;
      }

      const response = await axios.get(`${API_URL}/orders`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page,
          pageSize: isMobileRef.current ? 20 : 100,
          search,
          status,
          filters,
          streetViewOnly, // NEW PARAMETER
          statusOrderRosOnly, // NEW PARAMETER
        },
        signal,
      });

      if (
        search ||
        status ||
        filters !== "{}" ||
        streetViewOnly ||
        statusOrderRosOnly
      ) {
        // UPDATED
        setSearchResults(response.data.orders);
        setOrders([]);
      } else {
        setOrders(response.data.orders);
        setSearchResults([]);
      }

      setPagination(response.data.pagination);
      if (currentPage !== page) setCurrentPage(page);
    } catch (err) {
      console.error("Error fetching orders:", err);
      if (err.response?.status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        navigate("/");
      }
      setError("Failed to load orders. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const fetchQueueFiles = async () => {
    try {
      const response = await axios.get(`${API_URL}/queue/queue-files`);

      // Console the array of objects
      console.log("Queue Files:", response.data);
      setAccuzippQueue(response);
      // Or console each object separately
      response.data.forEach((file) => {
        console.log(`Order ID: ${file.orderId}, Name: ${file.name}`);
      });

      return response.data;
    } catch (error) {
      console.error("Error fetching queue files:", error);
      throw error;
    }
  };

  const handleSidebarCollapseToggle = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  // Fetch Analytics
  const fetchAnalytics = async () => {
    try {
      setAnalyticsLoading(true);
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/");
        return;
      }
      const response = await axios.get(`${API_URL}/orders/analytics`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAnalytics({
        totalOrders: response.data.totalOrders,
        completedOrders: response.data.completedOrders,
        processingOrders: response.data.processingOrders,
        pendingOrders: response.data.pendingOrders,
        statusOrderRosCount: response.data.statusOrderRosCount,
        streetViewRequiredCount: response.data.streetViewRequiredCount,
      });
    } catch (err) {
      console.error("Error fetching analytics:", err);
      setAnalytics({
        totalOrders: 0,
        completedOrders: 0,
        processingOrders: 0,
        pendingOrders: 0,
      });
    } finally {
      setAnalyticsLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  // Fetch Statuses
  const fetchOrderStatuses = async () => {
    try {
      setStatusesLoading(true);
      const token = localStorage.getItem("token");
      if (!token) return navigate("/");
      const response = await axios.get(`${API_URL}/orders/statuses`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAvailableStatuses(response.data.statuses || []);
    } catch (err) {
      console.error("Error fetching statuses:", err);
      setAvailableStatuses([]);
    } finally {
      setStatusesLoading(false);
    }
  };

  // Unified filter effect
  useEffect(() => {
    if (firstRenderRef.current) {
      firstRenderRef.current = false;
    }

    if (fetchController.current) {
      fetchController.current.abort();
    }

    const controller = new AbortController();
    fetchController.current = controller;

    const filtersQuery =
      Object.keys(columnFilters).length === 0
        ? "{}"
        : JSON.stringify(columnFilters);

    const fetchData = async () => {
      try {
        await fetchOrders(
          currentPage,
          searchTerm,
          statusFilter,
          filtersQuery,
          streetViewOnly,
          statusOrderRosOnly,
          controller.signal
        );
      } catch (err) {
        if (err.name !== "AbortError") console.error(err);
      }
    };

    const timeoutId = setTimeout(() => fetchData(), 50);
    return () => {
      clearTimeout(timeoutId);
      controller.abort();
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [
    currentPage,
    searchTerm,
    statusFilter,
    streetViewOnly,
    statusOrderRosOnly,
    columnFilters,
  ]);

  //  Initial fetch of statuses
  useEffect(() => {
    fetchOrderStatuses();
    fetchQueueFiles();
  }, []);

  // Handlers
  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/");
  };

  // Navigation handler
  const handleNavigation = (view) => {
    setActiveView(view);
    setIsMobileMenuOpen(false);
    if (view !== "search" && (searchTerm || statusFilter)) {
      setSearchTerm("");
      setStatusFilter("");
      setCurrentPage(1);
      setIsSearching(false);
    }
  };

  // Search input handler with debounce
  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setCurrentPage(1);
    }, 500);
  };

  // Status filter change handler
  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  // Refetch handler
  const handleRefetch = async () => {
    setCurrentPage(1);
    setSearchTerm("");
    setStatusFilter("");
    setIsSearching(false);
    setStreetViewOnly(false);
    setStatusOrderRosOnly(false);
    setColumnFilters({});
    fetchAnalytics();
    await fetchOrders(1, "", "", "{}", false); // Added false for streetViewOnly
  };

  // ✅ Unified filter handler (works for DateFilter too)
  const applyFilter = (key, filterParams) => {
    setColumnFilters((prev) => {
      const newFilters = { ...prev };

      if (!filterParams || filterParams.type === "clear") {
        delete newFilters[key];
      } else {
        newFilters[key] = filterParams; // ← Works for both filter types
      }

      return newFilters;
    });
    setCurrentPage(1);
  };

  // Mobile date filters extraction
  const mobileDateFilterKeys = [
    "orderDate",
    "accuzip_startTime",
    "accuzip_endTime",
    "streetViewCompletionDate",
  ];

  // Extract date-related filters for mobile view
  const mobileDateFilters = mobileDateFilterKeys.reduce((acc, key) => {
    if (columnFilters[key]) {
      acc[key] = columnFilters[key];
    }
    return acc;
  }, {});

  // Pagination page numbers logic
  const getPageNumbers = () => {
    const { page, totalPages } = pagination;
    if (totalPages <= 7)
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    let pages = [1];
    if (page > 3) pages.push("...");
    let rangeStart = Math.max(2, page - 1);
    let rangeEnd = Math.min(totalPages - 1, page + 1);
    if (page <= 3) rangeEnd = 4;
    else if (page >= totalPages - 2) rangeStart = totalPages - 3;
    for (let i = rangeStart; i <= rangeEnd; i++) pages.push(i);
    if (page < totalPages - 2) pages.push("...");
    pages.push(totalPages);
    return pages;
  };

  return (
    <>
      <GlobalStyle isDarkMode={isDarkMode} />
      <DashboardContainer isDarkMode={isDarkMode}>
        {/* Mobile Overlay */}
        <MobileOverlay
          isOpen={isMobileMenuOpen}
          onClick={() => setIsMobileMenuOpen(false)}
        />

        {/* Dashboard Sidebar Logic Handler */}
        <DashboardSidebar
          isOpen={isMobileMenuOpen}
          activeView={activeView}
          onNavigation={handleNavigation}
          onLogout={handleLogout}
          username={userDate?.username || "Admin"}
          userRole={userDate?.role || "user"}
          isSidebarCollapsed={isSidebarCollapsed}
          setIsSidebarCollapsed={setIsSidebarCollapsed}
          handleSidebarCollapseToggle={handleSidebarCollapseToggle}
          isDarkMode={isDarkMode}
        />
        <MainContent isSidebarCollapsed={isSidebarCollapsed}>
          <Header
            activeView={activeView}
            isMobileMenuOpen={isMobileMenuOpen}
            onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            onRefetch={handleRefetch}
            loading={loading}
            analyticsLoading={analyticsLoading}
            statusFilter={statusFilter}
            onStatusFilterChange={handleStatusFilterChange}
            setStatusFilter={setStatusFilter}
            statusesLoading={statusesLoading}
            availableStatuses={availableStatuses}
            searchTerm={searchTerm}
            onSearchInputChange={handleSearchInputChange}
            isDarkMode={isDarkMode}
            onThemeToggle={() => setIsDarkMode(!isDarkMode)}
          />

          {error && <ErrorMessage>{error}</ErrorMessage>}

          {(activeView === "dashboard" || activeView === "analytics") && (
            <Analytics
              analytics={analytics}
              activeView={activeView}
              loading={analyticsLoading}
              onStatusFilterChange={handleStatusFilterChange}
              onClearFilters={() => {
                setSearchTerm("");
                setStatusFilter("");
                setColumnFilters({});
                setCurrentPage(1);
                setIsSearching(false);
                setStreetViewOnly(false);
                setStatusOrderRosOnly(false);
              }}
              onStreetViewFilter={() => {
                setStreetViewOnly(true);
                setStatusFilter("");
                setStatusOrderRosOnly(false);
                setSearchTerm("");
                setCurrentPage(1);
              }}
              onStatusOrderRosOnly={() => {
                setStatusOrderRosOnly(true);
                setStreetViewOnly(false);
                setStatusFilter("");
                setSearchTerm("");
                setCurrentPage(1);
              }}
              // Add this new handler for status filter cards
              onStatusFilterClick={(status) => {
                setStatusFilter(status);
                setSearchTerm("");
                setColumnFilters({});
                setCurrentPage(1);
                setIsSearching(false);
                setStreetViewOnly(false);
                setStatusOrderRosOnly(false);
              }}
              isDarkMode={isDarkMode}
            />
          )}
          {activeView === "accuzip" && <AccuzipQueue isDarkMode={isDarkMode} />}
          {activeView === "realflow" && (
            <RealflowCsvPage isDarkMode={isDarkMode} />
          )}
          {activeView === "users" && username?.role !== "admin" && (
            <UsersManagement userDate={userDate} isDarkMode={isDarkMode} />
          )}
          {activeView !== "users" && activeView !== "realflow" && (
            <SearchResultsIndicator
              isSearching={isSearching}
              activeView={activeView}
              statusFilter={statusFilter}
              searchTerm={searchTerm}
              columnFilters={columnFilters}
              streetViewOnly={streetViewOnly}
              statusOrderRosOnly={statusOrderRosOnly}
              onClearFilters={() => {
                setSearchTerm("");
                setStatusFilter("");
                setColumnFilters({});
                setCurrentPage(1);
                setIsSearching(false);
                setStreetViewOnly(false);
                setStatusOrderRosOnly(false);
              }}
              isDarkMode={isDarkMode}
            />
          )}

          {(activeView === "dashboard" ||
            activeView === "orders" ||
            isSearching ||
            statusFilter) && (
            <>
              <OrdersTable
                orders={orders}
                onStreetViewFilter={() => {
                  setStreetViewOnly((prev) => !prev);
                  setStatusFilter("");
                  setSearchTerm("");
                  setCurrentPage(1);
                }}
                loading={loading}
                isSearching={isSearching}
                searchResults={searchResults}
                searchTerm={searchTerm}
                formatDate={formatDate}
                onApplyFilter={applyFilter}
                columnFilters={columnFilters}
                availableStatuses={availableStatuses}
                onStatusFilterChange={handleStatusFilterChange}
                statusFilter={statusFilter}
                isDarkMode={isDarkMode}
              />
              <MobileOrders
                orders={orders}
                loading={loading}
                isSearching={isSearching}
                searchResults={searchResults}
                searchTerm={searchTerm}
                formatDate={formatDate}
                dateFilters={mobileDateFilters}
                onDateFilterChange={applyFilter}
                setPagination={setPagination}
                statusFilter={statusFilter}
                availableStatuses={availableStatuses}
                onStatusFilterChange={handleStatusFilterChange}
                isDarkMode={isDarkMode}
              />
            </>
          )}

          {!loading &&
            activeView !== "analytics" &&
            activeView !== "accuzip" &&
            activeView !== "users" &&
            activeView !== "realflow" &&
            pagination.totalPages > 0 && (
              <Pagination
                pagination={pagination}
                onPageChange={(page) => setCurrentPage(page)}
                getPageNumbers={getPageNumbers}
                isMobile={isMobile}
                isDarkMode={isDarkMode}
              />
            )}
        </MainContent>
      </DashboardContainer>
    </>
  );
}

export default Dashboard;
